"use client";

import type { ReactNode } from "react";
import { buttonClass } from "./buttons";
import { DataUnavailable } from "./DataUnavailable";

/**
 * ScheduleScreen (docs/ux/screens/ScheduleScreen.md), shared by both frontends
 * (R22 T14).
 *
 * **The clinic's hours sit above the people, not beside them**, because since
 * H40 they *are* the calendar: every row below is those hours minus that
 * person's time away. Reading it the other way round, as though each
 * practitioner had their own pattern, is what the screen is shaped to prevent.
 *
 * A row is a link into one seat's page; nothing is edited here.
 */
export type ScheduleLink = (props: {
  href: string;
  className?: string;
  children: ReactNode;
}) => ReactNode;

export interface SeatRow {
  id: string;
  displayName: string;
  isYou: boolean;
  blackouts: number;
  raisedHours: number;
}

export function ScheduleOverview({
  gated,
  hours,
  rows,
  configured = true,
  Link,
}: {
  gated: boolean;
  /** Null when the read failed. */
  hours: { days: number; hours: number } | null;
  /** Null when the read failed. */
  rows: SeatRow[] | null;
  configured?: boolean;
  Link: ScheduleLink;
}) {
  return (
    <>
      <Link
        href="/business"
        className="mb-4 inline-block text-sm font-medium text-accent"
      >
        ← Back to your business
      </Link>
      <h1 className="mb-6 text-xl font-bold text-ink">Schedule</h1>

      {gated ? (
        // Tools gate (J2#2), ScheduleScreen element 2.
        <div className="rounded-xl border border-line bg-surface p-6 shadow-sm">
          <h2 className="mb-1 font-bold text-ink">
            Add your first service to unlock your schedule.
          </h2>
          <p className="mb-4 text-sm text-body">
            Your business tools unlock once at least one service exists.
          </p>
          <Link
            href="/business/services"
            className={buttonClass("primary", "inline-block")}
          >
            Add your first service
          </Link>
        </div>
      ) : rows === null || hours === null ? (
        <DataUnavailable configured={configured} what="your opening hours" />
      ) : (
        <>
          <div className="mb-5 rounded-xl border border-line bg-surface p-5 shadow-sm">
            <p className="font-semibold text-ink">
              {hours.hours > 0
                ? `Open ${hours.days} ${hours.days === 1 ? "day" : "days"}, ${hours.hours} ${hours.hours === 1 ? "hour" : "hours"} a week`
                : "No opening hours yet, so nobody can be booked"}
            </p>
            <p className="mt-1 text-sm text-muted">
              These hours are the calendar for everybody who works here.
            </p>
            <Link
              href="/business/hours"
              className="mt-3 inline-block text-sm font-semibold text-accent hover:underline"
            >
              Edit opening hours
            </Link>
          </div>

          {rows.length === 0 ? (
            <div className="rounded-xl border border-line bg-surface p-5 shadow-sm">
              <p className="font-semibold text-ink">Nobody works here yet.</p>
              <Link
                href="/business/practitioners"
                className="mt-3 inline-block text-sm font-semibold text-accent hover:underline"
              >
                Add a practitioner
              </Link>
            </div>
          ) : (
            <ul className="space-y-3">
              {rows.map((row) => (
                <li key={row.id}>
                  <Link
                    href={`/business/schedule/${row.id}`}
                    className="block rounded-xl border border-line bg-surface p-5 shadow-sm hover:border-accent"
                  >
                    <span className="font-semibold text-ink">
                      {row.displayName}
                      {row.isYou && (
                        <span className="ms-1 font-normal text-muted">
                          (you)
                        </span>
                      )}
                    </span>
                    <span className="ms-2 text-sm text-muted">
                      {row.blackouts > 0
                        ? `${row.blackouts} upcoming blackout${row.blackouts === 1 ? "" : "s"}`
                        : "Nothing scheduled away"}
                      {row.raisedHours > 0 &&
                        ` · ${row.raisedHours} hour${row.raisedHours === 1 ? "" : "s"} raised`}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </>
  );
}
